import { Helmet } from 'react-helmet-async';
import { useParams } from 'react-router-dom';

interface SurveyTemplateListSEOProps {
  templatesCount?: number;
  categories?: string[];
}

export const SurveyTemplateListSEO: React.FC<SurveyTemplateListSEOProps> = ({ 
  templatesCount = 0, 
  categories = [] 
}) => {
  const { lang } = useParams<{ lang?: string }>();
  const currentLang = lang || 'en';
  const prefix = `/${currentLang}`;
  
  const title = "Survey Templates - Professional HR & Employee Survey Templates | JiGSO EX Hub";
  const description = `Explore ${templatesCount}+ ready-to-use survey templates for HR professionals. Covering ${categories.slice(0, 3).join(', ')} and more. Start creating effective employee surveys in minutes.`;
  const url = `${window.location.origin}${prefix}/survey-templates`;

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="robots" content="index, follow" />
      <meta name="keywords" content="survey templates, employee surveys, HR tools, feedback templates, onboarding surveys, exit interviews, performance reviews" />
      <link rel="canonical" href={url} />
      
      {/* Open Graph Meta Tags */}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={url} />
      <meta property="og:site_name" content="JiGSO EX Hub" />
      
      {/* Twitter Card Meta Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      
      {/* Language and alternate links */}
      <meta httpEquiv="content-language" content={currentLang} />
      <link rel="alternate" hrefLang="en" href={`${window.location.origin}/en/survey-templates`} />
      <link rel="alternate" hrefLang="fr" href={`${window.location.origin}/fr/survey-templates`} />
      <link rel="alternate" hrefLang="x-default" href={`${window.location.origin}/en/survey-templates`} />
      
      {/* Structured Data for Collection Page */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          "name": "Survey Templates",
          "description": description,
          "url": url,
          "inLanguage": currentLang,
          "isPartOf": {
            "@type": "Website",
            "name": "JiGSO EX Hub",
            "url": window.location.origin
          },
          "publisher": {
            "@type": "Organization",
            "name": "JiGSO EX Hub",
            "url": window.location.origin
          },
          "numberOfItems": templatesCount,
          "about": {
            "@type": "Thing",
            "name": "Employee Surveys and HR Templates",
            "description": "Professional survey templates for human resources and employee experience management"
          }
        })}
      </script>
      
      {/* Breadcrumb Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Home",
              "item": `${window.location.origin}${prefix}`
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "Survey Templates",
              "item": url
            }
          ]
        })}
      </script>
    </Helmet>
  );
};